/* Storefront API (public) access for the directory: reads certified_installer
   metaobjects directly, no Worker required. Public token by design — safe to expose. */
const SPD_STOREFRONT_TOKEN = "8c400e6e9c1fbabcda2ef7fb69848bfa"; // Headless channel "Eowrap Headless"
const SPD_STOREFRONT_URL = "https://eowrap.com/api/2025-07/graphql.json";
const SPD_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes, sessionStorage
const SPD_SHOPS_QUERY = `query DirectoryShops($after: String) {
  metaobjects(type: "certified_installer", first: 250, after: $after) {
    nodes {
      handle
      shop_name: field(key: "shop_name") { value }
      listing_status: field(key: "listing_status") { value }
      verified_status: field(key: "verified_status") { value }
      city: field(key: "city") { value }
      state_province: field(key: "state_province") { value }
      country: field(key: "country") { value }
      business_address: field(key: "business_address") { value }
      google_maps_link: field(key: "google_maps_link") { value }
      services_offered: field(key: "services_offered") { value }
      main_image: field(key: "main_image") {
        reference { ... on MediaImage { image { url altText } } }
      }
      phone: field(key: "phone") { value }
      email: field(key: "email") { value }
      website: field(key: "website") { value }
      business_hours: field(key: "business_hours") { value }
      current_open_status: field(key: "current_open_status") { value }
      about_the_shop: field(key: "about_the_shop") { value }
      short_description: field(key: "short_description") { value }
      instagram_social_link: field(key: "instagram_social_link") { value }
    }
    pageInfo { hasNextPage endCursor }
  }
}`;

class ShopPartnersDirectory extends HTMLElement {
  constructor() {
    super();
    this.searchTimer = null;
    this.pageSize = 50;
  }

  connectedCallback() {
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";
    this.grid = this.querySelector("[data-partner-grid]");
    this.search = this.querySelector("[data-partner-search]");
    this.location = this.querySelector("[data-partner-location]");
    this.country = this.querySelector("[data-partner-country]");
    this.state = this.querySelector("[data-partner-state]");
    this.openOnly = this.querySelector("[data-partner-open]");
    this.verifiedOnly = this.querySelector("[data-partner-verified]");
    this.serviceOptions = this.querySelector("[data-partner-service-options]");
    this.clearButtons = Array.from(this.querySelectorAll("[data-partner-clear], [data-partner-empty-clear]"));
    this.count = this.querySelector("[data-partner-count]");
    this.empty = this.querySelector("[data-partner-empty]");
    this.sidebar = this.querySelector("[data-partner-sidebar]");
    this.filterToggle = this.querySelector("[data-partner-filter-toggle]");
    this.sidebarClose = this.querySelector("[data-partner-sidebar-close]");
    this.loadMoreButton = this.querySelector("[data-partner-load-more]");
    this.loadMoreControls = this.querySelector("[data-partner-load-more-controls]");
    this.showing = this.querySelector("[data-partner-showing]");
    this.pageSize = Number.parseInt(this.loadMoreButton?.dataset.pageSize || "50", 10);
    this.defaultLabels = {
      country: this.country?.options[0]?.textContent || "All countries",
      state: this.state?.options[0]?.textContent || "All states / provinces",
    };
    this.bindEvents();
    // 2026-10-17: Worker 代码已删除，目录完全走 Liquid 直出 + Storefront API 混合方案。
    this.initializeLiquidFallback();
    this.startStorefrontHydration();
  }

  initializeLiquidFallback() {
    this.cards = Array.from(this.querySelectorAll("[data-partner-card]"));
    // Verified installers first; stable sort keeps the original order within each group.
    this.cards.sort((a, b) => Number(b.dataset.verified === "true") - Number(a.dataset.verified === "true"));
    this.cards.forEach((card) => this.grid.appendChild(card));
    this.buildFallbackOptions();
    this.applyUrlSelections();
    this.visibleLimit = this.pageSize;
    this.filterFallback(false);
    this.openShopByHandle(this.hashShopHandle());
  }

  bindEvents() {
    [this.search, this.location].forEach((input) => input?.addEventListener("input", () => {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.filter(), 250);
    }));
    this.country?.addEventListener("change", () => { this.buildStateOptions(); this.filter(); });
    this.state?.addEventListener("change", () => this.filter());
    this.openOnly?.addEventListener("change", () => this.filter());
    this.verifiedOnly?.addEventListener("change", () => this.filter());
    this.serviceOptions?.addEventListener("change", () => this.filter());
    this.clearButtons.forEach((button) => button.addEventListener("click", () => this.reset()));
    this.filterToggle?.addEventListener("click", () => this.toggleSidebar(true));
    this.sidebarClose?.addEventListener("click", () => this.toggleSidebar(false));
    this.loadMoreButton?.addEventListener("click", () => {
      this.visibleLimit += this.pageSize;
      this.renderFallbackCards();
    });
    this.addEventListener("click", (event) => {
      const selectButton = event.target.closest("[data-partner-select-button]");
      if (selectButton) return this.toggleCustomSelect(selectButton);
      const option = event.target.closest("[data-partner-select-option]");
      if (option) return this.chooseCustomOption(option);
      const opener = event.target.closest("[data-partner-dialog-open]");
      if (opener) return this.openDialog(opener.dataset.partnerDialogOpen);
      const close = event.target.closest("[data-partner-dialog-close]");
      if (close) return close.closest("dialog")?.close();
      if (event.target.matches("dialog[data-partner-dialog]")) event.target.close();
    });
    this.addEventListener("close", (event) => {
      if (!event.target.matches("[data-partner-dialog]")) return;
      document.documentElement.classList.remove("shop-partners-dialog-open");
      event.target.closest("[data-partner-temp]")?.remove();
      this.clearShopHash();
      this.activeDialogOpener?.focus();
      this.activeDialogOpener = null;
    }, true);
    document.addEventListener("click", (event) => {
      if (!event.target.closest("[data-partner-custom-select]")) this.closeCustomSelects();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") { this.toggleSidebar(false); this.closeCustomSelects(); }
    });
    window.addEventListener("hashchange", () => {
      const handle = this.hashShopHandle();
      if (handle) this.openShopByHandle(handle);
      else this.querySelector("dialog[data-partner-dialog][open]")?.close();
    });
  }

  filter(updateUrl = true) {
    this.filterFallback(updateUrl);
  }

  buildStateOptions(selected = this.state?.value || "") {
    if (!this.state) return;
    const country = this.country?.value || "";
    const options = this.fallbackUniqueValues("state", country);
    this.populateSelect(this.state, options, this.defaultLabels.state, selected);
  }

  populateSelect(select, values, defaultLabel, selected = "") {
    if (!select) return;
    select.replaceChildren(new Option(defaultLabel, ""));
    values.forEach(({ value, label }) => select.add(new Option(label, value)));
    if ([...select.options].some((option) => option.value === selected)) select.value = selected;
    this.renderCustomSelect(select);
  }

  currentValues() {
    return { search: this.search?.value.trim() || "", location: this.location?.value.trim() || "", country: this.country?.value || "", state: this.state?.value || "", services: this.selectedServices().join(","), open: this.openOnly?.checked ? "1" : "", verified: this.verifiedOnly?.checked ? "1" : "" };
  }

  valuesFromUrl(params) {
    return { search: params.get("shop_search") || "", location: params.get("shop_location") || "", country: params.get("shop_country") || "", state: params.get("shop_state") || "", services: params.get("shop_services") || "", open: params.get("shop_open") === "1" ? "1" : "", verified: params.get("shop_verified") === "1" ? "1" : "" };
  }

  updateResults(shown, total, hasMore) {
    this.updateCount(total);
    if (this.empty) this.empty.hidden = total !== 0;
    if (this.loadMoreControls) this.loadMoreControls.hidden = total === 0;
    if (this.loadMoreButton) this.loadMoreButton.hidden = !hasMore;
    if (this.showing) {
      const template = this.showing.dataset.template || "Showing __SHOWN__ of __TOTAL__ shops";
      this.showing.textContent = template.replace("__SHOWN__", String(shown)).replace("__TOTAL__", String(total));
    }
  }

  cardTemplate(shop) {
    const id = `partner-dialog-${this.safeId(shop.handle)}`;
    const image = shop.main_image?.url ? `<img src="${this.escapeAttribute(this.safeUrl(shop.main_image.url))}" alt="${this.escapeAttribute(shop.main_image.alt || shop.shop_name)}" loading="lazy" width="1200" height="800">` : `<div class="shop-partners-directory__placeholder" aria-hidden="true"><span>EOWRAP</span></div>`;
    const location = [shop.city, shop.state_province].filter(Boolean).join(", ");
    const services = shop.services || [];
    const serviceTags = services.map((service) => `<span data-service-value="${this.escapeAttribute(this.handleize(service))}" data-service-label="${this.escapeAttribute(service)}">${this.escapeHtml(service)}</span>`).join("");
    const verified = shop.verified_status ? `<p class="shop-partners-directory__verified-line"><span aria-hidden="true">${this.icon("verified", 16)}</span>EOWRAP verified</p>` : "";
    const searchText = this.normalize([shop.shop_name, shop.city, shop.state_province, shop.country, shop.business_address, shop.short_description, (shop.services || []).join(" ")].filter(Boolean).join(" "));
    const locationText = this.normalize([shop.city, shop.state_province, shop.country].filter(Boolean).join(" "));
    return `<article class="shop-partners-directory__list-card" data-partner-card data-partner-handle="${this.escapeAttribute(shop.handle)}" data-search="${this.escapeAttribute(searchText)}" data-location="${this.escapeAttribute(locationText)}" data-country="${this.escapeAttribute(this.handleize(shop.country || ""))}" data-country-label="${this.escapeAttribute(shop.country || "")}" data-state="${this.escapeAttribute(this.handleize(shop.state_province || ""))}" data-state-label="${this.escapeAttribute(shop.state_province || "")}" data-verified="${shop.verified_status ? "true" : "false"}" data-open="${shop.current_open_status === "Open" ? "true" : "false"}"><div class="shop-partners-directory__list-media">${image}</div><div class="shop-partners-directory__list-content"><div class="shop-partners-directory__list-heading"><div><h3>${this.escapeHtml(shop.shop_name)}</h3>${verified}</div><div class="shop-partners-directory__list-location">${this.icon("address", 17)}<span>${this.escapeHtml(location)}</span></div></div>${services.length ? `<div class="shop-partners-directory__list-services">${serviceTags}</div>` : ""}${shop.short_description ? `<p class="shop-partners-directory__list-description">${this.escapeHtml(shop.short_description)}</p>` : ""}<button class="shop-partners-directory__list-direct-link" type="button" aria-haspopup="dialog" data-partner-dialog-open="${id}">View details</button></div>${this.dialogTemplate(shop, id, image, serviceTags)}</article>`;
  }

  dialogTemplate(shop, id, image, serviceTags) {
    const address = shop.business_address ? this.infoRow("address", "Address", this.escapeHtml(shop.business_address)) : "";
    const phoneHref = shop.phone ? `tel:${shop.phone.replace(/[^+\d]/g, "")}` : "";
    const phone = shop.phone ? this.infoRow("phone", "Phone", `<a href="${this.escapeAttribute(phoneHref)}">${this.escapeHtml(shop.phone)}</a>`) : "";
    const email = shop.email ? this.infoRow("email", "Email", `<a href="mailto:${this.escapeAttribute(shop.email)}">${this.escapeHtml(shop.email)}</a>`) : "";
    const websiteUrl = this.safeUrl(shop.website);
    const website = websiteUrl ? this.infoRow("web", "Visit website", `<a href="${this.escapeAttribute(websiteUrl)}" target="_blank" rel="noopener">${this.escapeHtml(shop.website.replace(/^https?:\/\//, ""))}</a>`) : "";
    const instagramLinks = Array.isArray(shop.instagram_social_link) ? shop.instagram_social_link : [];
    const instagramUrl = this.safeUrl(instagramLinks[0] || "");
    let instagram = "";
    if (instagramUrl) {
      const handle = String(instagramUrl.split("instagram.com/").pop() || "").split(/[/?#]/)[0];
      const label = handle && !handle.includes(":") ? `@${handle}` : instagramUrl.replace(/^https?:\/\/(www\.)?/, "");
      instagram = this.infoRow("instagram", "Instagram", `<a href="${this.escapeAttribute(instagramUrl)}" target="_blank" rel="noopener">${this.escapeHtml(label)}</a>`);
    }
    const hours = shop.business_hours ? this.infoRow("time", "Business hours", this.escapeHtml(shop.business_hours).replace(/\n/g, "<br>")) : "";
    const maps = this.safeUrl(shop.google_maps_link) || (shop.business_address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shop.business_address)}` : "");
    const contact = phoneHref || (shop.email ? `mailto:${shop.email}` : websiteUrl);
    return `<dialog class="shop-partners-directory__dialog" id="${id}" aria-labelledby="${id}-title" data-partner-dialog data-partner-handle="${this.escapeAttribute(shop.handle)}"><div class="shop-partners-directory__dialog-shell"><button class="shop-partners-directory__dialog-close" type="button" aria-label="Close shop details" data-partner-dialog-close><svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2L2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 12L2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button><div class="shop-partners-directory__dialog-layout"><div class="shop-partners-directory__dialog-main"><div class="shop-partners-directory__dialog-hero">${image}<div class="shop-partners-directory__dialog-title"><h2 id="${id}-title">${this.escapeHtml(shop.shop_name)}</h2></div></div>${shop.about_the_shop ? `<section><h3>About the shop</h3><p>${this.escapeHtml(shop.about_the_shop).replace(/\n/g, "<br>")}</p></section>` : ""}${serviceTags ? `<section><h3>Services offered</h3><div class="shop-partners-directory__list-services">${serviceTags}</div></section>` : ""}</div><aside class="shop-partners-directory__dialog-sidebar"><div class="shop-partners-directory__dialog-info"><h3>Contact info</h3>${address}${phone}${email}${website}${instagram}${hours}</div><div class="shop-partners-directory__dialog-actions">${maps ? `<a href="${this.escapeAttribute(maps)}" target="_blank" rel="noopener">${this.icon("direction", 19)}Get directions</a>` : ""}${contact ? `<a class="shop-partners-directory__dialog-contact" href="${this.escapeAttribute(contact)}">${this.icon("phone", 19)}Contact</a>` : ""}</div></aside></div></div></dialog>`;
  }

  infoRow(icon, label, content) { return `<p>${this.icon(icon, 20)}<span><strong>${label}</strong>${content}</span></p>`; }
  icon(name, size) { const url = this.dataset[`icon${name[0].toUpperCase()}${name.slice(1)}`]; return url ? `<img src="${this.escapeAttribute(url)}" alt="" width="${size}" height="${size}" aria-hidden="true">` : ""; }
  buildFallbackOptions() {
    this.populateSelect(this.country, this.fallbackUniqueValues("country"), this.defaultLabels.country);
    this.buildStateOptions();
    const services = new Map();
    this.cards.forEach((card) => card.querySelectorAll("[data-service-value]").forEach((item) => services.set(item.dataset.serviceValue, item.dataset.serviceLabel)));
    if (this.serviceOptions) this.serviceOptions.innerHTML = [...services].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => `<label class="shop-partners-directory__checkbox"><input type="checkbox" value="${this.escapeAttribute(value)}" data-partner-service-checkbox><span>${this.escapeHtml(label)}</span></label>`).join("");
  }

  fallbackUniqueValues(key, country = "") {
    const values = new Map();
    this.cards.forEach((card) => { if (country && card.dataset.country !== country) return; const value = card.dataset[key]; const label = card.dataset[`${key}Label`]; if (value && label) values.set(value, label); });
    return [...values].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }

  filterFallback(updateUrl = true) {
    const values = this.currentValues();
    const services = values.services.split(",").filter(Boolean);
    this.visibleLimit = this.pageSize;
    this.filteredCards = this.cards.filter((card) => {
      const cardServices = [...card.querySelectorAll("[data-service-value]")].map((item) => item.dataset.serviceValue);
      return (!values.search || this.normalize(card.dataset.search).includes(this.normalize(values.search))) && (!values.location || this.normalize(card.dataset.location).includes(this.normalize(values.location))) && (!values.country || card.dataset.country === values.country) && (!values.state || card.dataset.state === values.state) && (!services.length || services.every((service) => cardServices.includes(service))) && (!values.open || card.dataset.open === "true") && (!values.verified || card.dataset.verified === "true");
    });
    this.renderFallbackCards();
    if (updateUrl) this.updateUrl();
  }

  renderFallbackCards() {
    const shown = Math.min(this.visibleLimit, this.filteredCards.length);
    const visible = new Set(this.filteredCards.slice(0, shown));
    this.cards.forEach((card) => { card.hidden = !visible.has(card); });
    this.updateResults(shown, this.filteredCards.length, shown < this.filteredCards.length);
  }

  applyUrlSelections() {
    const values = this.valuesFromUrl(new URLSearchParams(window.location.search));
    if (this.search) this.search.value = values.search;
    if (this.location) this.location.value = values.location;
    this.selectValue(this.country, values.country);
    this.buildStateOptions(values.state);
    if (this.openOnly) this.openOnly.checked = values.open === "1";
    if (this.verifiedOnly) this.verifiedOnly.checked = values.verified === "1";
    const services = new Set(values.services.split(",").filter(Boolean));
    this.querySelectorAll("[data-partner-service-checkbox]").forEach((input) => { input.checked = services.has(input.value); });
  }

  selectedServices() { return [...this.querySelectorAll("[data-partner-service-checkbox]:checked")].map((input) => input.value); }
  updateCount(value) { if (this.count) this.count.textContent = `${value} ${value === 1 ? this.count.dataset.single : this.count.dataset.plural} ${this.count.dataset.found || ""}`.trim(); }

  reset() {
    if (this.search) this.search.value = "";
    if (this.location) this.location.value = "";
    if (this.country) this.country.value = "";
    this.buildStateOptions("");
    if (this.openOnly) this.openOnly.checked = false;
    if (this.verifiedOnly) this.verifiedOnly.checked = false;
    this.querySelectorAll("[data-partner-service-checkbox]").forEach((input) => { input.checked = false; });
    this.syncAllCustomSelects();
    this.updateUrl();
    this.filter(false);
    this.toggleSidebar(false);
  }

  updateUrl() {
    const url = new URL(window.location.href);
    const values = this.currentValues();
    const map = { shop_search: values.search, shop_location: values.location, shop_country: values.country, shop_state: values.state, shop_services: values.services, shop_open: values.open, shop_verified: values.verified };
    Object.entries(map).forEach(([key, value]) => value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
    window.history.replaceState({}, "", url);
  }

  renderCustomSelect(select) {
    const custom = select?.closest("[data-partner-custom-select]");
    const menu = custom?.querySelector("[data-partner-select-menu]");
    if (!menu) return;
    menu.innerHTML = [...select.options].map((option) => `<button type="button" class="shop-partners-directory__select-option" data-partner-select-option data-value="${this.escapeAttribute(option.value)}" role="option" aria-selected="${option.value === select.value}">${this.escapeHtml(option.textContent)}</button>`).join("");
    this.syncCustomSelection(select);
  }
  toggleCustomSelect(button) { const custom = button.closest("[data-partner-custom-select]"); const menu = custom?.querySelector("[data-partner-select-menu]"); const open = button.getAttribute("aria-expanded") !== "true"; this.closeCustomSelects(); button.setAttribute("aria-expanded", String(open)); if (menu) menu.hidden = !open; }
  chooseCustomOption(option) { const custom = option.closest("[data-partner-custom-select]"); const select = custom?.querySelector("select"); if (!select) return; select.value = option.dataset.value || ""; this.syncCustomSelection(select); select.dispatchEvent(new Event("change", { bubbles: true })); this.closeCustomSelects(); }
  syncCustomSelection(select) { const custom = select?.closest("[data-partner-custom-select]"); const selected = select?.options[select.selectedIndex]; const label = custom?.querySelector("[data-partner-select-label]"); if (label && selected) label.textContent = selected.textContent; custom?.querySelectorAll("[data-partner-select-option]").forEach((option) => option.setAttribute("aria-selected", String(option.dataset.value === select.value))); }
  syncAllCustomSelects() { this.querySelectorAll("[data-partner-custom-select] select").forEach((select) => this.renderCustomSelect(select)); }
  closeCustomSelects() { this.querySelectorAll("[data-partner-custom-select]").forEach((custom) => { custom.querySelector("[data-partner-select-button]")?.setAttribute("aria-expanded", "false"); const menu = custom.querySelector("[data-partner-select-menu]"); if (menu) menu.hidden = true; }); }
  selectValue(select, value) { if (!select || !value || ![...select.options].some((option) => option.value === value)) return; select.value = value; this.syncCustomSelection(select); }
  toggleSidebar(open) { if (!this.sidebar || !this.filterToggle) return; this.sidebar.classList.toggle("is-open", open); this.filterToggle.setAttribute("aria-expanded", String(open)); }
  openDialog(id) { const dialog = id ? this.querySelector(`#${CSS.escape(id)}`) : null; if (!dialog) return; this.activeDialogOpener = this.querySelector(`[data-partner-dialog-open="${CSS.escape(id)}"]`); document.documentElement.classList.add("shop-partners-dialog-open"); dialog.showModal(); this.setShopHash(dialog.dataset.partnerHandle); }
  hashShopHandle() { const match = window.location.hash.match(/^#shop\/([A-Za-z0-9_-]+)/); return match ? match[1] : ""; }
  setShopHash(handle) { if (!handle) return; const url = new URL(window.location.href); url.hash = `shop/${handle}`; window.history.replaceState({}, "", url); }
  clearShopHash() { if (!this.hashShopHandle()) return; const url = new URL(window.location.href); url.hash = ""; window.history.replaceState({}, "", url); }
  openShopByHandle(handle) {
    if (!handle) return;
    const existing = this.querySelector(`dialog[data-partner-handle="${CSS.escape(handle)}"]`);
    if (existing) {
      const card = existing.closest("[data-partner-card]");
      if (card) { card.hidden = false; card.scrollIntoView({ block: "center" }); }
      this.openDialog(existing.id);
      return;
    }
  }  /* ---- Storefront API hydration: full 2044-shop dataset without the Worker ---- */
  startStorefrontHydration() {
    if (this.storefrontHydrated || this.storefrontHydrating) return;
    this.storefrontHydrating = true;
    (async () => {
      try {
        let shops = this.readStorefrontCache();
        if (!shops) {
          shops = await this.fetchAllShopsStorefront();
          this.writeStorefrontCache(shops);
        }
        this.mergeStorefrontShops(shops);
        this.storefrontHydrated = true;
        this.openShopByHandle(this.hashShopHandle());
      } catch (error) {
        console.error("Storefront hydration failed; keeping server-rendered cards.", error);
      } finally {
        this.storefrontHydrating = false;
      }
    })();
  }

  async fetchAllShopsStorefront() {
    const shops = [];
    let after = null;
    for (let page = 0; page < 20; page++) {
      const data = await this.storefrontRequest(SPD_SHOPS_QUERY, { after });
      const connection = data && data.metaobjects;
      if (!connection) break;
      for (const node of connection.nodes || []) {
        const shop = this.mapStorefrontShop(node);
        if (shop.handle && shop.listing_status === "published") shops.push(shop);
      }
      if (!connection.pageInfo || !connection.pageInfo.hasNextPage) break;
      after = connection.pageInfo.endCursor;
    }
    return shops;
  }

  async storefrontRequest(query, variables) {
    const response = await fetch(SPD_STOREFRONT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": SPD_STOREFRONT_TOKEN },
      body: JSON.stringify({ query, variables }),
    });
    if (!response.ok) throw new Error(`Storefront API ${response.status}`);
    const payload = await response.json();
    if (payload.errors && payload.errors.length) throw new Error(payload.errors[0].message || "Storefront API error");
    return payload.data;
  }

  mapStorefrontShop(node) {
    const value = (aliased) => (aliased && aliased.value) || "";
    const listValue = (aliased) => {
      const raw = value(aliased);
      if (!raw) return [];
      try { const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed : [parsed]; }
      catch { return [raw]; }
    };
    const imageRef = node.main_image && node.main_image.reference;
    return {
      handle: node.handle || "",
      shop_name: value(node.shop_name),
      listing_status: value(node.listing_status).toLowerCase(),
      verified_status: value(node.verified_status) === "true",
      city: value(node.city),
      state_province: value(node.state_province),
      country: value(node.country),
      business_address: value(node.business_address),
      google_maps_link: value(node.google_maps_link),
      services: listValue(node.services_offered).map(String),
      main_image: imageRef && imageRef.image ? { url: imageRef.image.url, alt: imageRef.image.altText || value(node.shop_name) } : null,
      phone: value(node.phone),
      email: value(node.email),
      website: value(node.website),
      business_hours: value(node.business_hours),
      current_open_status: value(node.current_open_status),
      about_the_shop: value(node.about_the_shop),
      short_description: value(node.short_description),
      instagram_social_link: listValue(node.instagram_social_link).map(String),
    };
  }

  mergeStorefrontShops(shops) {
    if (!Array.isArray(shops) || !shops.length || !this.grid) return;
    const existing = new Set(Array.from(this.querySelectorAll("[data-partner-handle]")).map((el) => el.dataset.partnerHandle));
    const fresh = shops.filter((shop) => shop.handle && !existing.has(shop.handle));
    if (fresh.length) this.grid.insertAdjacentHTML("beforeend", fresh.map((shop) => this.cardTemplate(shop)).join(""));
    // Re-collect all cards, keep verified-first ordering, rebuild filter
    // options from the full dataset, then re-apply the current filter.
    const prevCountry = this.country ? this.country.value : "";
    const prevState = this.state ? this.state.value : "";
    this.cards = Array.from(this.querySelectorAll("[data-partner-card]"));
    this.cards.sort((a, b) => Number(b.dataset.verified === "true") - Number(a.dataset.verified === "true"));
    this.cards.forEach((card) => this.grid.appendChild(card));
    this.buildFallbackOptions();
    if (prevCountry) this.selectValue(this.country, prevCountry);
    this.buildStateOptions(prevState);
    this.syncAllCustomSelects();
    this.filterFallback(false);
  }

  readStorefrontCache() {
    try {
      const raw = sessionStorage.getItem("spd-shops-v1");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.shops) || !parsed.shops.length) return null;
      if (Date.now() - parsed.savedAt > SPD_CACHE_TTL_MS) return null;
      return parsed.shops;
    } catch { return null; }
  }

  writeStorefrontCache(shops) {
    try {
      sessionStorage.setItem("spd-shops-v1", JSON.stringify({ savedAt: Date.now(), shops }));
    } catch { /* storage unavailable or full: skip caching */ }
  }

  safeUrl(value) { if (!value) return ""; try { const url = new URL(value, window.location.origin); return ["http:", "https:"].includes(url.protocol) ? url.href : ""; } catch { return ""; } }
  safeId(value) { return String(value || "shop").replace(/[^a-zA-Z0-9_-]/g, "-"); }
  handleize(value) { return this.normalize(value).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
  normalize(value) { return String(value || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim(); }
  escapeHtml(value) { const div = document.createElement("div"); div.textContent = value ?? ""; return div.innerHTML; }
  escapeAttribute(value) { return this.escapeHtml(value).replace(/"/g, "&quot;"); }
}

if (!customElements.get("shop-partners-directory")) customElements.define("shop-partners-directory", ShopPartnersDirectory);
